import React, { useEffect, useMemo, useState } from "react";
import { compose, graphql } from "react-apollo";

import TableView, {
  buyerTwoIconCellObject,
  DetailLinkCell,
  EmailCell,
  includes,
  PhoneCell,
  stringComparator,
  TextHeader,
  TextObjectWithMapper,
  ActionCell
} from "../../components/new-table";
import FadeIn from "../../components/fade-in";
import { phoneDisplay } from "../../utils/helpers";
import routes_constants from "../layout/routes";
import { GET_REALTORS } from "../../graphql/queries";
import { FaUser } from "react-icons/fa";

/** @module GlobalRealtorsTableModule */
/**
 * This component is a table used to display realtors data based on the global search query. It contains only the top 5 results and further specific query needs to be made
 * to get the appropriate data. It also has a basic hover action on Name column which redirect to the realtor's details.
 *
 * @param {Object} props this contains the data from the graphql query and the function to close the drawer
 * @param {String} props.searchTerm it contains the search query from the GlobalSearchDetails component
 * @param {Function} props.handleDrawerClose function to close the drawer
 * @param {Object} props.data it contains array of prospects to be shown on table
 * @param {Function} props.setMergeRealtors it sets the data to display on top of the form
 * @returns {JSX.Element} It returns jsx containing the table title and the table itself
 */
const GlobalRealtorsTable = ({ handleDrawerClose, data, setMergeData }) => {
  const [loading, setLoading] = useState(true);
  const [render, setRender] = useState(false);
  const [realtors, setRealtors] = useState({});

  /**
   * Either set the data in state or set the loading to inform the table what to render.
   */
  useEffect(() => {
    if (!data.loading && data.getRealtors && data.getRealtors.realtors) {
      setLoading(false);
      setRender(true);
      setRealtors(data.getRealtors.realtors);
    }
    if (data.loading) {
      setLoading(data.loading);
    }
  }, [data]);

  /**
 * This function adds realtors to the merge realtor list on clicking the merge button. If the realtor selected is already
 * in the merge realtor list then the realtor is not added a second time.
 * @param {Object} realtor Realtor data which is to be added into the merged realtor list
 */
  const updateMergeList = (realtor) => {
    setMergeData(prevState => {
      const data = prevState.type === "customer" ? [realtor]
        : prevState.data?.find(realtorList => realtorList.id === realtor.id)
          ? prevState.data
          : [...prevState.data, realtor]
      return {
        data,
        type: "realtor"
      }
    })
  }


  /**
   * this function is used to map the community from the field
   * @param {object} field rows of the table
   * @param {string} accessor object attribute name
   * @param {string} type inner object attribute name
   * @function
   * @inner
   * @memberof module:GlobalRealtorsTableModule
   * @returns {array}
   */
  const realtorCommunityMapper = (field, accessor, type) => {
    return field[accessor].filter((val) => val).reverse();
  };

  /**
   * Columns of the table containing a column to show an icon in case there is a second buyer, name, community, phone and email.
   * @constant
   * @memberof module:GlobalRealtorsTableModule
   */
  const columns = useMemo(() => [
    {
      id: "0",
      header: "",
      accessor: "",
      component: TextHeader,
      cell: buyerTwoIconCellObject,
    },
    {
      id: "1",
      header: "Name",
      accessor: "first_name",
      component: TextHeader,
      sortable: false,
      cell: {
        component: DetailLinkCell,
        path: (field, accessor) => routes_constants.REALTOR_DETAIL,
        mapper: (field, accessor) => field[accessor] + " " + field["last_name"],
        action: handleDrawerClose
      },
    },
    {
      id: "2",
      header: "Community",
      accessor: "Communities",
      component: TextHeader,
      csvMapper: (field, accessor) =>
        realtorCommunityMapper(field, accessor, "name")
          .map((item) => item.name)
          .join(","),
      cell: {
        component: TextObjectWithMapper,
        mapper: (field, accessor) =>
          realtorCommunityMapper(field, accessor, "name"),
      },
    },
    {
      id: "3",
      header: "Phone",
      accessor: "cell_phone",
      component: TextHeader,
      sortable: false,
      filter: includes,
      csvMapper: (field, accessor) => phoneDisplay(field[accessor]) || "",
      cell: { component: PhoneCell },
    },
    {
      id: "4",
      header: "Email",
      accessor: "primary_email",
      component: TextHeader,
      sortable: false,
      comparator: stringComparator,
      cell: {
        component: EmailCell,
        check: "primary_email_status",
      },
    },
  ]);




  const hoverActions = React.useMemo(
    () => [
      {
        textValue: 'Merge',
        component: ActionCell,
        action: (event) => {
          updateMergeList(event);
        },
      }
    ],
    []
  );


  return (
    <div>
      <div className="d-flex header-table">
        <FaUser className="header-icon" />
        <div className="d-flex align-self-center header-text">
          Realtors {!realtors.length && "(No records Found)"}
        </div>
      </div>
      {realtors.length ? (
        <div className="results-table-search">
          <FadeIn show={render}>
            <TableView
              columns={columns}
              data={realtors}
              loading={loading}
              currentPage={0}
              isGlobalSearch={true}
              hoverActions={hoverActions}
              controlled={true}
            />
          </FadeIn>
        </div>
      ) : null}
    </div>
  );
};

export default compose(
  graphql(GET_REALTORS, {
    options: ({ searchTerm }) => {
      return {
        variables: {
          pageNum: 0,
          limit: 5,
          sort: null,
          filter: {
            text: searchTerm || "",
          },
        },
        fetchPolicy: "cache-and-network",
      };
    },
  })
)(GlobalRealtorsTable);
